import React from "react";
import { useLang } from "@/context/LanguageContext";
import { useCurrency } from "@/context/CurrencyContext";
import { arDigits, fmtLocaleNum, fmtIsoWeekLabel } from "@/lib/utils";

/**
 * V2.0 P2 — Auto-interpretation block "What this week means".
 *
 * Zero-backend deterministic computation. Reads three fields already
 * present on the aggregated weekly + summary docs:
 *   • Composition   = pure-sales share of all DLD procedures (this week vs YTD)
 *   • Scale         = tx_count and total_aed_bn vs the trailing 4-week average
 *   • Distribution  = off-plan share of DLD procedures (this week vs YTD)
 *
 * Language is confined to a neutral whitelist:
 *   EN: "higher than" · "lower than" · "in line with" · "accounted for"
 *   AR: "أعلى من"     · "أدنى من"    · "قريب من"        · "شكّلت"
 *
 * No sentiment vocabulary is permitted (market-mood words are forbidden),
 * and every claim is followed by the actual comparison number, so the
 * reader can verify the math against the raw DLD figures on the same page.
 */
const IN_LINE_REL_TOL = 0.05; // ±5% relative → "in line with"

const _verdict = (cur, ref) => {
  if (cur == null || !ref) return "na";
  const delta = (cur - ref) / ref;
  if (Math.abs(delta) <= IN_LINE_REL_TOL) return "in_line";
  return delta > 0 ? "higher" : "lower";
};

const _verdictWord = (v, isAR) => {
  switch (v) {
    case "higher": return isAR ? "أعلى من" : "higher than";
    case "lower":  return isAR ? "أدنى من" : "lower than";
    case "in_line":return isAR ? "قريب من" : "in line with";
    default:       return isAR ? "غير متوفر" : "not available";
  }
};

const _pct = (v, isAR, digits = 1) => {
  if (v == null || Number.isNaN(v)) return "—";
  const rounded = Number(v).toFixed(digits);
  return isAR ? `${arDigits(rounded)}٪` : `${rounded}%`;
};

const _bn = (v, isAR) => {
  if (v == null) return "—";
  const num = Number(v).toFixed(2);
  return isAR ? `${arDigits(num)} مليار د.إ` : `AED ${num}B`;
};

// Format an ISO date string (YYYY-MM-DD) as "Sep 14, 2026" for the
// English "What this week means" date-range caption. Uses UTC so DLD
// week boundaries are not shifted by the reader's timezone.
const _MONTHS_EN = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
const _fmtHumanDate = (iso) => {
  if (!iso || typeof iso !== "string") return "—";
  const parts = iso.split("-");
  if (parts.length !== 3) return iso;
  const y = parts[0];
  const m = _MONTHS_EN[parseInt(parts[1], 10) - 1] || parts[1];
  const d = parseInt(parts[2], 10);
  return `${m} ${d}, ${y}`;
};

const Tile = ({ label, value, sentence, footnote, verdictClass, testid }) => (
  <div
    className="rounded-2xl border hairline bg-white/80 p-4 sm:p-5 flex flex-col gap-2"
    data-testid={testid}
  >
    <p className="font-mono-brand text-[9px] sm:text-[10px] tracking-[0.18em] uppercase text-propertic-gold/90 leading-snug min-h-[2.4em]">
      {label}
    </p>
    <p className={`font-editorial text-xl sm:text-2xl leading-tight tabular-nums ${verdictClass || "text-propertic-navy"}`}>
      {value}
    </p>
    <p className="text-[12px] sm:text-[12.5px] text-propertic-ink-2/95 leading-relaxed">
      {sentence}
    </p>
    {footnote && (
      <p className="mt-1 font-mono-brand text-[9px] tracking-[0.14em] uppercase text-propertic-muted/80 leading-relaxed">
        {footnote}
      </p>
    )}
  </div>
);

/**
 * @param {object}   props.week          Current week doc (from /insights/weekly/{iso})
 * @param {object[]} props.priorWeeks    Weeks strictly BEFORE `week`, DESC-sorted by iso_week
 * @param {object}   props.summary       Summary doc (from /insights/hub or /dld-test/summary)
 * @param {"compact"|"expanded"} props.variant
 */
export default function WeeklyInterpretation({ week, priorWeeks = [], summary, variant = "expanded" }) {
  const { lang } = useLang();
  const isAR = lang === "ar";

  if (!week || !summary) return null;

  const ytd = summary.ytd || summary;

  // Composition — sales share of all DLD procedures
  const wkSalesShare = week.tx_count ? (100 * (week.sales_count || 0)) / week.tx_count : null;
  const ytdSalesShare = ytd.total_transactions ? (100 * (ytd.sales_count || 0)) / ytd.total_transactions : null;
  const compV = _verdict(wkSalesShare, ytdSalesShare);

  // Scale — trailing 4-week average of tx_count + total_aed_bn (skip null AED weeks)
  const trailing = (priorWeeks || []).slice(0, 4);
  const trailingTx = trailing.filter((w) => w.tx_count != null);
  const trailingAed = trailing.filter((w) => w.total_aed_bn != null);
  const avgTx = trailingTx.length ? trailingTx.reduce((s, w) => s + w.tx_count, 0) / trailingTx.length : null;
  const avgAed = trailingAed.length ? trailingAed.reduce((s, w) => s + w.total_aed_bn, 0) / trailingAed.length : null;
  const scaleTxV = _verdict(week.tx_count, avgTx);
  const scaleAedV = week.total_aed_bn == null ? "na" : _verdict(week.total_aed_bn, avgAed);

  // Distribution — off-plan share of all DLD procedures (this week vs YTD)
  const wkOffplan = week.offplan_share_pct;
  const ytdOffplan = ytd.offplan_share_pct_all_tx;
  const distV = _verdict(wkOffplan, ytdOffplan);

  const verdictColor = (v) => {
    // Neutral palette — no green/red bias to avoid implying "good/bad".
    // Muted navy for movements, gold for headline value, muted grey for na/in-line.
    if (v === "higher" || v === "lower") return "text-propertic-navy";
    if (v === "in_line") return "text-propertic-muted";
    return "text-propertic-muted";
  };

  // ------- Composition tile -------
  const compositionTile = {
    label: isAR ? "التركيبة · حصة المبيعات" : "Composition · Sales share",
    value: _pct(wkSalesShare, isAR),
    sentence: (
      isAR
        ? (<>
            {"شكّلت المبيعات العقارية "}<span className="font-semibold">{_pct(wkSalesShare, isAR)}</span>
            {" من إجمالي إجراءات دائرة الأراضي هذا الأسبوع، "}
            <span className="font-semibold">{_verdictWord(compV, isAR)}</span>
            {" المتوسط منذ بداية العام ("}{_pct(ytdSalesShare, isAR)}{")."}
          </>)
        : (<>
            {"Pure property sales "}<span className="font-semibold">accounted for {_pct(wkSalesShare, isAR)}</span>
            {" of DLD procedures this week — "}
            <span className="font-semibold">{_verdictWord(compV, isAR)}</span>
            {" the YTD share of "}{_pct(ytdSalesShare, isAR)}{"."}
          </>)
    ),
    footnote: isAR ? "= مبيعات ÷ (مبيعات + رهون + هبات)" : "= sales ÷ (sales + mortgages + gifts)",
    verdictClass: verdictColor(compV),
    testid: "wkinsight-composition",
  };

  // ------- Scale tile -------
  const scaleValue = fmtLocaleNum(week.tx_count, isAR);
  const avgTxDisplay = avgTx == null ? "—" : fmtLocaleNum(Math.round(avgTx), isAR);
  const avgAedDisplay = avgAed == null ? "—" : _bn(avgAed, isAR);
  const scaleTile = {
    label: isAR ? "الحجم · مقارنةً بمتوسط الأسابيع الأربعة السابقة" : "Scale · vs trailing 4-week average",
    value: scaleValue,
    sentence: (
      isAR
        ? (<>
            {"سُجِّلت "}<span className="font-semibold">{scaleValue}</span>{" إجراءً بقيمة "}
            <span className="font-semibold">{_bn(week.total_aed_bn, isAR)}</span>
            {" — "}
            <span className="font-semibold">{_verdictWord(scaleTxV, isAR)}</span>
            {" متوسط الأسابيع الأربعة السابقة ("}{avgTxDisplay}{" إجراءً · "}{avgAedDisplay}{")."}
          </>)
        : (<>
            <span className="font-semibold">{scaleValue}</span>{" procedures totalling "}
            <span className="font-semibold">{_bn(week.total_aed_bn, isAR)}</span>
            {" — "}
            <span className="font-semibold">{_verdictWord(scaleTxV, isAR)}</span>
            {" the trailing 4-week average of "}{avgTxDisplay}{" procedures · "}{avgAedDisplay}{"."}
          </>)
    ),
    footnote: isAR
      ? "المتوسط يستثني هذا الأسبوع؛ ٤ أسابيع سابقة"
      : "Trailing average excludes the current week (4 prior weeks)",
    verdictClass: verdictColor(scaleTxV),
    testid: "wkinsight-scale",
  };

  // ------- Distribution tile -------
  const distributionTile = {
    label: isAR ? "التوزيع · حصة قيد الإنشاء" : "Distribution · Off-plan share",
    value: _pct(wkOffplan, isAR),
    sentence: (
      isAR
        ? (<>
            {"شكّلت الصفقات قيد الإنشاء "}<span className="font-semibold">{_pct(wkOffplan, isAR)}</span>
            {" من الإجراءات هذا الأسبوع، "}
            <span className="font-semibold">{_verdictWord(distV, isAR)}</span>
            {" حصة قيد الإنشاء منذ بداية العام ("}{_pct(ytdOffplan, isAR)}{")."}
          </>)
        : (<>
            {"Off-plan transactions "}<span className="font-semibold">accounted for {_pct(wkOffplan, isAR)}</span>
            {" of DLD procedures this week — "}
            <span className="font-semibold">{_verdictWord(distV, isAR)}</span>
            {" the YTD off-plan share of "}{_pct(ytdOffplan, isAR)}{"."}
          </>)
    ),
    footnote: isAR
      ? "= إجراءات قيد الإنشاء ÷ إجمالي إجراءات دائرة الأراضي"
      : "= off-plan procedures ÷ all DLD procedures",
    verdictClass: verdictColor(distV),
    testid: "wkinsight-distribution",
  };

  const isCompact = variant === "compact";

  return (
    <section
      className={
        isCompact
          ? "mt-8 sm:mt-10 rounded-3xl border hairline bg-propertic-sand/40 p-5 sm:p-6"
          : "mt-2 mb-10 rounded-3xl border hairline bg-propertic-sand/40 p-6 sm:p-8"
      }
      data-testid={isCompact ? "insights-interpretation-compact" : "insights-interpretation-expanded"}
      aria-label={isAR ? "ماذا يعني هذا الأسبوع" : "What this week means"}
    >
      <div className="flex items-baseline justify-between gap-3 mb-3 sm:mb-4">
        <div>
          <p className="font-mono-brand text-[10px] tracking-[0.2em] uppercase text-propertic-gold mb-1">
            {isAR ? "قراءة الأسبوع · مبنية على البيانات" : "Reading this week · Data-driven"}
          </p>
          <h2 className={`font-editorial ${isCompact ? "text-xl sm:text-2xl" : "text-2xl sm:text-3xl"} leading-[1.15] text-propertic-navy`}>
            {isAR
              ? <>ماذا يعني هذا الأسبوع <span className="italic-serif text-propertic-gold">({fmtIsoWeekLabel(week.iso_week, true)})</span>؟</>
              : <>What this week means <span className="italic-serif text-propertic-gold">({week.iso_week})</span></>}
          </h2>
          {/* Explicit start–end date range so the reader can immediately
              see what "W38" actually covers without having to look up the
              ISO calendar. Rendered as a compact caption under the H2,
              same rhythm as the rest of the block, no layout impact. */}
          {(week.start_date || week.end_date) && (
            <p
              className="mt-1 font-mono-brand text-[10px] sm:text-[11px] tracking-[0.14em] uppercase text-propertic-ink-2/80"
              data-testid="wkinsight-date-range"
            >
              {isAR
                ? `${arDigits(week.start_date || "")} — ${arDigits(week.end_date || "")}`
                : `${_fmtHumanDate(week.start_date)} — ${_fmtHumanDate(week.end_date)}`}
            </p>
          )}
        </div>
      </div>
      <p className={`text-[13px] sm:text-[14px] text-propertic-ink-2/95 leading-relaxed mb-4 sm:mb-5 ${isCompact ? "" : "max-w-3xl"}`}>
        {isAR
          ? "قراءة رقمية آلية مبنية بالكامل على سجلات دائرة الأراضي والأملاك للأسبوع الحالي مقارنةً بالمتوسط منذ بداية العام والأسابيع الأربعة السابقة. لا نستخدم أي مصطلحات ذات حكم قيمي على السوق."
          : "An automated numerical reading built entirely from DLD records for this week compared against the year-to-date share and the 4-week trailing average. No market-sentiment vocabulary is used, every claim is backed by the exact figure alongside it."}
      </p>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 sm:gap-5">
        <Tile {...compositionTile} />
        <Tile {...scaleTile} />
        <Tile {...distributionTile} />
      </div>
      <p className="mt-4 sm:mt-5 font-mono-brand text-[9px] tracking-[0.16em] uppercase text-propertic-muted/80">
        {isAR
          ? `المصدر · دائرة الأراضي والأملاك · بيانات مفتوحة · تسامح "قريب من" ±٥٪`
          : `Source · Dubai Land Department Open Data · "In line with" tolerance ±5%`}
      </p>
    </section>
  );
}
